/**
 * Memory tab (§4.4.4).
 *
 * Left: fixed 240px file tree.
 * Right: CodeMirror 6 markdown editor fills the rest.
 *
 * Dirty-state tracking is per-file so switching files doesn't lose
 * in-progress edits. A beforeunload listener warns on tab close with
 * unsaved content. ⌘S / Ctrl-S commits the current file.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Save, FileWarning, Loader2 } from 'lucide-react';
import type { AgentConfig } from '@/api/agents';
import { useMemoryFiles, useMemoryFile, useSaveMemoryFile } from '@/hooks/useMemoryFiles';
import { MemoryFileTree } from '@/components/MemoryFileTree';
import { MemoryEditor, type MemoryEditorHandle } from '@/components/MemoryEditor';
import { Button } from '@/components/ui/button';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { MEMORY_FILE_TEMPLATES } from '@/api/memory';
import { ApiError } from '@/api/client';

interface MemoryProps {
  agent: AgentConfig;
}

export default function Memory({ agent }: MemoryProps) {
  const filesQuery = useMemoryFiles(agent.name);
  const [selected, setSelected] = useState<string | undefined>(undefined);

  // Auto-select the first real file on the server once the list lands.
  // Falls back to the first template name when the agent has no files
  // yet (fresh agent, first visit) so the editor has something to show.
  useEffect(() => {
    if (selected) return;
    const realFiles = filesQuery.data?.files ?? [];
    if (realFiles.length > 0) {
      setSelected(realFiles[0].filename);
      return;
    }
    if (filesQuery.isSuccess) {
      setSelected(MEMORY_FILE_TEMPLATES[0]);
    }
  }, [filesQuery.data, filesQuery.isSuccess, selected]);

  const fileQuery = useMemoryFile(agent.name, selected);
  const save = useSaveMemoryFile(agent.name);

  // Per-file dirty buffer. Keyed by filename so tab-switching works.
  const [buffers, setBuffers] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<MemoryEditorHandle>(null);

  // Hydrate buffer from server once per file.
  useEffect(() => {
    if (!fileQuery.data) return;
    const filename = fileQuery.data.filename;
    setBuffers((prev) => (prev[filename] != null ? prev : { ...prev, [filename]: fileQuery.data!.content }));
  }, [fileQuery.data]);

  // beforeunload guard on dirty buffers.
  useEffect(() => {
    if (dirty.size === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty.size]);

  const handleSave = useCallback(async () => {
    if (!fileQuery.data) return;
    const filename = fileQuery.data.filename;
    const content = buffers[filename] ?? fileQuery.data.content;
    setError(null);
    try {
      await save.mutateAsync({
        filename,
        content,
        headSha: fileQuery.data.head_sha,
      });
      setDirty((cur) => {
        const next = new Set(cur);
        next.delete(filename);
        return next;
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    }
  }, [buffers, fileQuery.data, save]);

  function handleChange(value: string) {
    if (!fileQuery.data) return;
    const filename = fileQuery.data.filename;
    setBuffers((cur) => ({ ...cur, [filename]: value }));
    setDirty((cur) => {
      if (value === fileQuery.data?.content) {
        const next = new Set(cur);
        next.delete(filename);
        return next;
      }
      const next = new Set(cur);
      next.add(filename);
      return next;
    });
  }

  function handleSelect(filename: string) {
    if (filename === selected) return;
    // Preserve the dirty buffer if the user navigates away mid-edit;
    // no in-app "unsaved changes" modal for v1, but the visible dot
    // on the tree row still flags which files are unsaved.
    setSelected(filename);
    setError(null);
  }

  const currentBuffer =
    fileQuery.data && buffers[fileQuery.data.filename] != null
      ? buffers[fileQuery.data.filename]!
      : fileQuery.data?.content ?? '';

  const currentDirty = fileQuery.data ? dirty.has(fileQuery.data.filename) : false;

  return (
    <div className="flex h-[calc(100vh-14rem)] min-h-[32rem] border rounded-md overflow-hidden">
      <MemoryFileTree
        files={filesQuery.data?.files}
        selected={selected ?? ''}
        onSelect={handleSelect}
        dirtyFiles={dirty}
      />
      <div className="flex flex-1 flex-col min-w-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-mono">{selected}</span>
            {currentDirty && (
              <span className="inline-flex items-center gap-1 text-xs text-warning">
                <FileWarning className="h-3 w-3" />
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!currentDirty || save.isPending || !fileQuery.data}
            >
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
        <div className="relative flex-1 min-h-0">
          {fileQuery.isLoading && (
            <div className="p-4">
              <LoadingSkeleton className="h-full w-full" />
            </div>
          )}
          {fileQuery.isError && (
            <div className="p-4 text-sm text-destructive">
              Could not load <code>{selected}</code>:{' '}
              {fileQuery.error instanceof ApiError
                ? fileQuery.error.message
                : 'unknown error'}
            </div>
          )}
          {!fileQuery.isLoading && !fileQuery.isError && (
            <MemoryEditor
              ref={editorRef}
              value={currentBuffer}
              onChange={handleChange}
              onSave={handleSave}
              language="markdown"
              className="absolute inset-0 rounded-none border-0"
            />
          )}
          {error && (
            <p className="absolute bottom-2 left-2 right-2 rounded bg-destructive/10 p-2 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
